import {
  ArrowCounterClockwise,
  Check,
  EyeSlash,
  Gift,
  LinkSimple,
  PencilSimple,
  Trash,
  UsersThree,
} from '@phosphor-icons/react';
import { FAMILY_WISHES, LEISURE_TEXT, seesClaims, type Wish } from '@shh/shared';
import {
  Avatar,
  Button,
  ChoiceGroup,
  Heading,
  Icon,
  IconButton,
  MultiChoice,
  Sheet,
  Surface,
  Text,
  cn,
} from '@shh/ui';
import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { useLens } from '../../app/lens';
import { WishForm } from '../../components/leisureForms';
import { claimWish, deleteWish, setWishReceived } from '../../data/api';
import { memberById, parents } from '../../data/derive';
import { useFamily, type WithId } from '../../data/family';
import { Empty, useLeisureRights } from './common';
import { locale } from '../../lib/locale';
import { funText } from '../../i18n/fun';

/** "Lottas Wünsche" – with the German genitive that families actually write. */
const listTitle = funText.listTitle;

/**
 * One wish list: a person's or the family's shared one. Adults see who gets what ("Ich besorge
 * das") – kids never (the rules keep it from their devices), and nobody on their own list.
 */
export function WishesPage() {
  const { owner = '' } = useParams();
  const f = useFamily();
  const lens = useLens();
  const rights = useLeisureRights();
  const [occasion, setOccasion] = useState<'all' | Wish['occasion']>('all');
  const [editing, setEditing] = useState<WithId<Wish> | 'new' | null>(null);
  const member = memberById(f, owner);
  if (owner !== FAMILY_WISHES && !member) return <Navigate to="/freizeit" replace />;

  const own = owner === (f.meId ?? lens.member?.id);
  // on the wall with the PIN the person picked at the top counts as the viewer
  const secret = seesClaims({ adult: rights.adult, memberId: f.meId ?? lens.member?.id }, owner);
  const editable = rights.editsWishes(owner);
  const wishes = f.wishes
    .filter((w) => w.owner === owner && (occasion === 'all' || w.occasion === occasion))
    .sort((a, b) => b.love - a.love || a.title.localeCompare(b.title, 'de'));
  const open = wishes.filter((w) => !w.received);
  const got = wishes.filter((w) => w.received);
  const editWish = editing && editing !== 'new' ? editing : undefined;
  const lists = [...f.members.map((m) => m.id), FAMILY_WISHES];

  return (
    <>
      <nav aria-label={funText.wishLists} className="-mx-gutter overflow-x-auto px-gutter">
        <ul className="flex gap-gap-sm">
          {lists.map((id) => {
            const m = memberById(f, id);
            const current = id === owner;
            return (
              <li key={id}>
                <Link
                  to={`/freizeit/wuensche/${id}`}
                  aria-current={current ? 'page' : undefined}
                  className={cn(
                    'flex min-h-touch-min items-center gap-gap-xs rounded-chip border-2 py-1 pr-inset-sm pl-1 font-semibold outline-none',
                    'focus-visible:outline-3 focus-visible:outline-border-focus',
                    current
                      ? 'border-action-primary bg-accent-secondary-soft'
                      : 'border-border-subtle bg-surface-raised',
                  )}
                >
                  {m ? (
                    <Avatar name={m.name} color={m.color} emoji={m.emoji} size="sm" decorative />
                  ) : (
                    <span className="grid size-avatar-sm place-items-center rounded-avatar bg-accent-secondary text-accent-on-accent">
                      <Icon icon={UsersThree} size="sm" weight="fill" />
                    </span>
                  )}
                  {m ? m.name : funText.family}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <section aria-labelledby="list" className="flex flex-col gap-gap-md">
        <div className="flex flex-wrap items-center justify-between gap-gap-sm">
          <Heading level={2} size="md" id="list">
            {member ? (own ? funText.myWishes : listTitle(member.name)) : funText.familyWishes}
          </Heading>
          {editable && (
            <Button icon={Gift} onPress={() => setEditing('new')}>
              {funText.wish}
            </Button>
          )}
        </div>
        <ChoiceGroup
          label={funText.forWhat}
          value={occasion}
          onChange={setOccasion}
          choices={[
            { value: 'all', label: funText.all },
            { value: 'birthday', label: funText.birthday },
            { value: 'christmas', label: funText.christmas },
          ]}
        />
        {open.length ? (
          <ul className="grid grid-cols-1 gap-gap-md md:grid-cols-2 xl:grid-cols-3">
            {open.map((w) => (
              <li key={w.id} className="flex">
                <WishCard
                  wish={w}
                  secret={secret}
                  onEdit={editable ? () => setEditing(w) : undefined}
                />
              </li>
            ))}
          </ul>
        ) : (
          <Empty icon={Gift}>
            {owner === FAMILY_WISHES
              ? funText.familyEmpty
              : own
                ? funText.ownEmpty
                : funText.otherEmpty(member?.name ?? '')}
          </Empty>
        )}
        {(own || !rights.adult) && (
          <Text size="sm" tone="muted" className="flex items-start gap-gap-xs">
            <Icon icon={EyeSlash} size="sm" className="mt-0.5 shrink-0" />
            {funText.surprise}
          </Text>
        )}
      </section>

      {got.length > 0 && (
        <section aria-labelledby="got" className="flex flex-col gap-gap-md">
          <Heading level={2} size="md" id="got">
            {funText.got}
          </Heading>
          <ul className="flex flex-col gap-gap-sm">
            {got.map((w) => (
              <li key={w.id}>
                <Surface tone="sunken" padding="sm" className="flex items-center gap-gap-sm">
                  <Icon icon={Check} size="md" className="text-feedback-success" />
                  <span className="flex-1 font-semibold">{w.title}</span>
                  {editable && (
                    <IconButton
                      icon={ArrowCounterClockwise}
                      aria-label={funText.backOnWishes(w.title)}
                      onPress={() => void setWishReceived(f.householdId, w.id, false)}
                    />
                  )}
                </Surface>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Sheet
        isOpen={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editWish ? funText.editWish : funText.newWish}
        isDismissable={false}
      >
        {editing && (
          <div className="flex flex-col gap-gap-lg">
            <WishForm owner={owner} wish={editWish} onDone={() => setEditing(null)} />
            {editWish && (
              <div className="flex flex-wrap gap-gap-sm">
                <Button
                  variant="secondary"
                  icon={Check}
                  onPress={() => {
                    void setWishReceived(f.householdId, editWish.id, true);
                    setEditing(null);
                  }}
                >
                  {funText.got}
                </Button>
                <Button
                  variant="quiet"
                  icon={Trash}
                  onPress={() => {
                    void deleteWish(f.householdId, editWish.id, rights.adult);
                    setEditing(null);
                  }}
                >
                  {funText.deleteWish}
                </Button>
              </div>
            )}
          </div>
        )}
      </Sheet>
    </>
  );
}

function WishCard({
  wish: w,
  secret,
  onEdit,
}: {
  wish: WithId<Wish>;
  secret: boolean;
  onEdit?: () => void;
}) {
  const f = useFamily();
  const [choosing, setChoosing] = useState(false);
  const claim = secret ? f.claims[w.id] : undefined;
  const adults = parents(f);
  const meta = [w.price, w.occasion !== 'any' && LEISURE_TEXT[locale].occasion[w.occasion]].filter(
    Boolean,
  );
  const by = (ids: string[]) =>
    ids
      .map((id) => memberById(f, id)?.name)
      .filter(Boolean)
      .join(funText.and);
  // a parent's own account claims for themselves; the wall asks who
  const claimMine = () =>
    f.meId ? void claimWish(f.householdId, w.id, [f.meId]) : setChoosing(true);

  return (
    <Surface as="article" className="flex w-full flex-col gap-gap-sm">
      <div className="flex items-start gap-gap-sm">
        <div className="flex min-w-0 flex-1 flex-col">
          <Heading level={3} size="sm">
            {w.title}
          </Heading>
          <Text size="sm" tone="secondary">
            <span
              className="font-bold text-reward-kudos"
              aria-label={funText.loveOf(String(w.love))}
            >
              {'♥'.repeat(w.love)}
            </span>
            {w.love === 3 && funText.heartWish}
            {meta.length > 0 && ` · ${meta.join(' · ')}`}
          </Text>
        </div>
        {w.link && (
          <a
            href={w.link}
            target="_blank"
            rel="noreferrer noopener"
            className="flex min-h-touch-min items-center gap-gap-xs rounded-control px-inset-xs font-semibold underline outline-none focus-visible:outline-3 focus-visible:outline-border-focus"
          >
            <Icon icon={LinkSimple} size="sm" /> {funText.link}
          </a>
        )}
        {onEdit && (
          <IconButton icon={PencilSimple} aria-label={funText.editItem(w.title)} onPress={onEdit} />
        )}
      </div>
      {secret &&
        (claim ? (
          <div className="mt-auto flex flex-wrap items-center gap-gap-sm rounded-control bg-feedback-success-surface px-inset-sm py-inset-xs">
            <span className="flex-1 text-sm font-bold text-feedback-success">
              {funText.gets(by(claim.by), claim.by.length)}
            </span>
            <Button variant="quiet" onPress={() => void claimWish(f.householdId, w.id, [])}>
              {funText.notAfterAll}
            </Button>
          </div>
        ) : (
          <div className="mt-auto flex flex-wrap gap-gap-sm pt-gap-xs">
            <Button variant="secondary" icon={Gift} onPress={claimMine}>
              {f.meId ? funText.iGetIt : funText.whoGets}
            </Button>
            {f.meId && adults.length > 1 && (
              <Button variant="quiet" icon={UsersThree} onPress={() => setChoosing(true)}>
                {funText.together}
              </Button>
            )}
          </div>
        ))}
      <ClaimSheet
        isOpen={choosing}
        wish={w}
        onClose={() => setChoosing(false)}
        initial={f.meId ? adults.map((a) => a.id) : []}
      />
    </Surface>
  );
}

function ClaimSheet({
  isOpen,
  wish,
  initial,
  onClose,
}: {
  isOpen: boolean;
  wish: WithId<Wish>;
  initial: string[];
  onClose: () => void;
}) {
  const f = useFamily();
  const [who, setWho] = useState<string[]>(initial);
  return (
    <Sheet
      isOpen={isOpen}
      onOpenChange={(o) => !o && onClose()}
      title={funText.whoGetsThis(wish.title)}
    >
      <div className="flex flex-col gap-gap-lg">
        <MultiChoice
          label={funText.whoGives}
          layout="tile"
          value={who}
          onChange={setWho}
          choices={parents(f).map((m) => ({
            value: m.id,
            label: m.name,
            picture: <Avatar name={m.name} color={m.color} emoji={m.emoji} size="sm" decorative />,
          }))}
        />
        <Text size="sm" tone="muted">
          {funText.adultsOnly(memberById(f, wish.owner)?.name ?? funText.theKids)}
        </Text>
        <Button
          size="lg"
          icon={Check}
          isDisabled={!who.length}
          onPress={() => {
            void claimWish(f.householdId, wish.id, who);
            onClose();
          }}
        >
          {funText.remember}
        </Button>
      </div>
    </Sheet>
  );
}
